import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import type { ResourceKind } from "@checklist/shared";
import {
  ApiError,
  createAccessRequest,
  fetchResourcePreview,
  type ApiResourcePreview,
} from "../lib/api.js";
import { useAuth } from "../context/AuthContext.js";
import {
  AuthShell,
  ErrorBanner,
  fieldCls,
  labelCls,
  SuccessBanner,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";

function resourceKind(value: string | undefined): ResourceKind | null {
  return value === "list" || value === "template" || value === "checklist"
    ? value
    : null;
}

function kindLabel(kind: ResourceKind): string {
  return kind === "checklist" ? "Checklist" : kind === "template" ? "Template" : "List";
}

function resourcePath(kind: ResourceKind, id: string): string {
  const segment = kind === "list" ? "lists" : kind === "template" ? "templates" : "checklists";
  return `/${segment}/${id}`;
}

export function RequestAccessPage() {
  const { kind: rawKind, id } = useParams<{ kind: string; id: string }>();
  const kind = resourceKind(rawKind);
  const { user } = useAuth();
  const [preview, setPreview] = useState<ApiResourcePreview | null>(null);
  const [loading, setLoading] = useState(Boolean(kind && id));
  const [email, setEmail] = useState(user?.email ?? "");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [alreadyHasAccess, setAlreadyHasAccess] = useState(false);

  useEffect(() => {
    setEmail((current) => current || user?.email || "");
  }, [user?.email]);

  useEffect(() => {
    if (!kind || !id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchResourcePreview(kind, id);
        if (!cancelled) setPreview(result);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setError(err instanceof ApiError && err.status === 404 ? "unavailable" : "load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, kind]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!kind || !id) return;
    setBusy(true);
    setError(null);
    try {
      const result = await createAccessRequest({
        kind,
        id,
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      if (result.status === "already_has_access") {
        setAlreadyHasAccess(true);
        return;
      }
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError("You've made several requests recently - try again tomorrow.");
      } else {
        setError(err instanceof ApiError ? err.message : "Could not send your request.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (!kind || !id || error === "unavailable") {
    return (
      <AuthShell title="This list isn't available">
        <p className="text-sm text-muted">
          It may have been removed or the link is no longer valid.
        </p>
        <Link className="mt-5 inline-block text-sm font-medium text-brand-700 hover:underline" to="/">
          Return home
        </Link>
      </AuthShell>
    );
  }

  if (loading) {
    return (
      <AuthShell title="Loading access request">
        <p className="text-sm text-muted">Just a moment...</p>
      </AuthShell>
    );
  }

  if (!preview) {
    return (
      <AuthShell title="We couldn't load this resource">
        <p className="text-sm text-muted">Please try again in a moment.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={`Request access to ${preview.name}`}>
      <div className="rounded-xl border border-line bg-slate-50 p-4">
        <span className="inline-flex rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700">
          {kindLabel(preview.kind)}
        </span>
        <p className="mt-2 font-semibold text-ink">{preview.name}</p>
        <p className="mt-1 text-sm text-muted">
          {preview.itemCount} item{preview.itemCount === 1 ? "" : "s"} - owned by{" "}
          {preview.ownerScreenName}
        </p>
      </div>

      {alreadyHasAccess ? (
        <div className="mt-5">
          <SuccessBanner message="You already have access to this resource." />
          <Link
            className="mt-3 inline-block text-sm font-medium text-brand-700 hover:underline"
            to={resourcePath(kind, id)}
          >
            Open {kindLabel(kind).toLowerCase()}
          </Link>
        </div>
      ) : sent ? (
        <div className="mt-5">
          <SuccessBanner
            message={`We've let ${preview.ownerScreenName} know. You'll get an email when they respond.`}
          />
        </div>
      ) : (
        <form className="mt-5 space-y-4" onSubmit={(event) => void onSubmit(event)}>
          <ErrorBanner message={error === "load" ? "Could not load the preview. Please refresh and try again." : error} />
          <div>
            <label className={labelCls} htmlFor="requestEmail">
              Email
            </label>
            <input
              id="requestEmail"
              className={fieldCls}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="requestName">
              Your name <span className="text-muted-sm">(optional)</span>
            </label>
            <input
              id="requestName"
              className={fieldCls}
              value={name}
              maxLength={40}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="requestMessage">
              Message <span className="text-muted-sm">(optional)</span>
            </label>
            <textarea
              id="requestMessage"
              className={`${fieldCls} min-h-28 resize-y`}
              value={message}
              maxLength={500}
              onChange={(event) => setMessage(event.target.value)}
            />
            <p className="mt-1 text-right text-xs text-muted-sm">{message.length}/500</p>
          </div>
          <Button type="submit" fullWidth disabled={busy}>
            {busy ? "Sending request..." : "Request access"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
